import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Plane, Building, MapPin, Car, CalendarDays } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ICONS = { flight: Plane, hotel: Building, activity: MapPin, transfer: Car, itinerary: CalendarDays };

const EMPTY_FLIGHT = { airline: '', flightNumber: '', pnr: '', departureCity: '', departureAirport: '', departureTerminal: '', departureDate: '', departureTime: '', arrivalCity: '', arrivalAirport: '', arrivalTerminal: '', arrivalDate: '', arrivalTime: '', travelClass: 'Economy', baggage: '', seatNumber: '' };
const EMPTY_HOTEL = { hotelName: '', address: '', checkIn: '', checkOut: '', roomType: '', mealPlan: '', confirmationNumber: '', contactPhone: '', specialRequests: '' };
const EMPTY_ACTIVITY = {
  activityName: '', date: '', time: '', duration: '', pickupPoint: '',
  guideName: '', guidePhone: '', description: '', inclusions: '',
  // Phase 8.21 operational fields
  voucherRef: '', partnerRef: '', operatorRef: '',
  leadGuestName: '', totalPax: '', adults: '', children: '',
  voucherType: '', validity: '', reportingTime: '',
  instantConfirmation: '', reservationRequired: '',
  hotline: '', address: '', meetingPoint: '',
  cancellationPolicy: '',
};
const EMPTY_TRANSFER = { transferType: 'airport', pickup: '', drop: '', date: '', time: '', vehicleType: '', driverName: '', driverPhone: '' };
const EMPTY_ITINERARY = { itineraryId: '', summary: '', dayWise: [] };

const VoucherForm = ({ tripId, trip, type, editing, onSaved, onCancel }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => {
    if (editing) return {
      guestName: editing.guestName || '', destination: editing.destination || '',
      travelDateFrom: editing.travelDateFrom || '', travelDateTo: editing.travelDateTo || '',
      status: editing.status || 'draft', notes: editing.notes || '',
      termsAndConditions: editing.termsAndConditions || '',
      hideCancellationPolicy: !!editing.hideCancellationPolicy,
      showOperator: editing.showOperator !== false,
      operatorOverride: editing.operatorOverride || '',
      flight: editing.flight || EMPTY_FLIGHT, hotel: editing.hotel || EMPTY_HOTEL,
      activity: editing.activity || EMPTY_ACTIVITY, transfer: editing.transfer || EMPTY_TRANSFER,
      itinerary: editing.itinerary || EMPTY_ITINERARY,
    };
    return {
      guestName: trip?.guestName || '', destination: trip?.destination || '',
      travelDateFrom: trip?.travelStartDate || '', travelDateTo: trip?.travelEndDate || '',
      status: 'draft', notes: '', termsAndConditions: '',
      hideCancellationPolicy: false,
      showOperator: true,
      operatorOverride: '',
      flight: EMPTY_FLIGHT, hotel: EMPTY_HOTEL, activity: EMPTY_ACTIVITY, transfer: EMPTY_TRANSFER, itinerary: EMPTY_ITINERARY,
    };
  });

  // Auto-load default T&C for new vouchers
  React.useEffect(() => {
    if (!editing && type) {
      axios.get(`${BACKEND_URL}/api/vouchers/default-terms/${type}`)
        .then(r => { if (r.data?.terms) setForm(prev => ({ ...prev, termsAndConditions: prev.termsAndConditions || r.data.terms })); })
        .catch(() => {});
    }
  }, [type, editing]);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const setNested = (section, key, val) => setForm(prev => ({ ...prev, [section]: { ...prev[section], [key]: val } }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { tripId, type, ...form };
    try {
      if (editing) {
        await axios.put(`${BACKEND_URL}/api/vouchers/${editing.voucherId}`, payload);
        toast.success('Voucher updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/vouchers`, payload);
        toast.success('Voucher created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const Icon = ICONS[type] || CalendarDays;

  return (
    <div className="space-y-6" data-testid="voucher-form-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onCancel}><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#2C3E35]/10 flex items-center justify-center">
            <Icon className="h-5 w-5 text-[#2C3E35]" strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {editing ? 'Edit' : 'New'} {type.charAt(0).toUpperCase() + type.slice(1)} Voucher
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Common Fields */}
        <Card className="p-6">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">General Information</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><Label>Guest Name</Label><Input value={form.guestName} onChange={e => set('guestName', e.target.value)} className="mt-1" data-testid="voucher-guest-name" /></div>
            <div><Label>Destination</Label><Input value={form.destination} onChange={e => set('destination', e.target.value)} className="mt-1" data-testid="voucher-destination" /></div>
            <div><Label>Travel From</Label><Input type="date" value={form.travelDateFrom} onChange={e => set('travelDateFrom', e.target.value)} className="mt-1" /></div>
            <div><Label>Travel To</Label><Input type="date" value={form.travelDateTo} onChange={e => set('travelDateTo', e.target.value)} className="mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => set('status', v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={e => set('notes', e.target.value)} className="mt-1" /></div>
          </div>
          {type === 'activity' && (
            <div className="mt-4 space-y-3" data-testid="activity-admin-controls">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-testid="hide-cancellation-toggle-label">
                <input
                  type="checkbox"
                  checked={!!form.hideCancellationPolicy}
                  onChange={(e) => set('hideCancellationPolicy', e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300"
                  data-testid="hide-cancellation-toggle"
                />
                <span>Hide Cancellation Policy from client view (still kept internally)</span>
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-testid="show-operator-toggle-label">
                <input
                  type="checkbox"
                  checked={form.showOperator !== false}
                  onChange={(e) => set('showOperator', e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300"
                  data-testid="show-operator-toggle"
                />
                <span>Show Operator Name on the voucher</span>
              </label>
              <div className="pl-6 max-w-md">
                <Label className="text-xs text-slate-500">Operator Name (manual override — leave blank to use auto-extracted)</Label>
                <Input
                  value={form.operatorOverride || ''}
                  onChange={(e) => set('operatorOverride', e.target.value)}
                  className="mt-1"
                  placeholder="e.g., Atlantis Dubai, Rayna Tours, Klook"
                  data-testid="operator-override-input"
                />
              </div>
            </div>
          )}
        </Card>

        {/* Type-specific fields */}
        {type === 'flight' && <FlightFields data={form.flight} onChange={(k, v) => setNested('flight', k, v)} />}
        {type === 'hotel' && <HotelFields data={form.hotel} onChange={(k, v) => setNested('hotel', k, v)} />}
        {type === 'activity' && <ActivityFields data={form.activity} onChange={(k, v) => setNested('activity', k, v)} />}
        {type === 'transfer' && <TransferFields data={form.transfer} onChange={(k, v) => setNested('transfer', k, v)} />}
        {type === 'itinerary' && <ItineraryFields data={form.itinerary} onChange={(k, v) => setNested('itinerary', k, v)} />}

        {/* Terms & Cancellation Policy */}
        <Card className="p-6" data-testid="voucher-terms-card">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Terms & Cancellation Policy</h3>
          <textarea value={form.termsAndConditions} onChange={e => set('termsAndConditions', e.target.value)}
            className="w-full min-h-[140px] border rounded-md p-3 text-sm text-slate-700 leading-relaxed"
            placeholder="Terms and cancellation policy for this voucher..."
            data-testid="voucher-terms-input" />
          <p className="text-[11px] text-slate-400 mt-1.5">Default terms are auto-filled based on voucher type. You can customise them per voucher.</p>
        </Card>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onCancel} className="rounded-sm">Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="save-voucher-btn" className="bg-[#2C3E35] hover:bg-[#1E2B25] text-white rounded-sm">
            {saving ? 'Saving...' : editing ? 'Update Voucher' : 'Create Voucher'}
          </Button>
        </div>
      </form>
    </div>
  );
};

const FlightFields = ({ data, onChange }) => (
  <Card className="p-6" data-testid="flight-fields">
    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Flight Details</h3>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      <div><Label>Airline *</Label><Input value={data.airline} onChange={e => onChange('airline', e.target.value)} className="mt-1" data-testid="flight-airline" required /></div>
      <div><Label>Flight Number *</Label><Input value={data.flightNumber} onChange={e => onChange('flightNumber', e.target.value)} className="mt-1" data-testid="flight-number" required /></div>
      <div><Label>PNR *</Label><Input value={data.pnr} onChange={e => onChange('pnr', e.target.value)} className="mt-1" data-testid="flight-pnr" required /></div>
      <div><Label>Departure City</Label><Input value={data.departureCity} onChange={e => onChange('departureCity', e.target.value)} className="mt-1" /></div>
      <div><Label>Departure Airport</Label><Input value={data.departureAirport} onChange={e => onChange('departureAirport', e.target.value)} className="mt-1" /></div>
      <div><Label>Terminal</Label><Input value={data.departureTerminal} onChange={e => onChange('departureTerminal', e.target.value)} className="mt-1" /></div>
      <div><Label>Departure Date</Label><Input type="date" value={data.departureDate} onChange={e => onChange('departureDate', e.target.value)} className="mt-1" /></div>
      <div><Label>Departure Time</Label><Input type="time" value={data.departureTime} onChange={e => onChange('departureTime', e.target.value)} className="mt-1" /></div>
      <div><Label>Class</Label>
        <Select value={data.travelClass} onValueChange={v => onChange('travelClass', v)}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Economy">Economy</SelectItem>
            <SelectItem value="Premium Economy">Premium Economy</SelectItem>
            <SelectItem value="Business">Business</SelectItem>
            <SelectItem value="First">First</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div><Label>Arrival City</Label><Input value={data.arrivalCity} onChange={e => onChange('arrivalCity', e.target.value)} className="mt-1" /></div>
      <div><Label>Arrival Airport</Label><Input value={data.arrivalAirport} onChange={e => onChange('arrivalAirport', e.target.value)} className="mt-1" /></div>
      <div><Label>Arrival Terminal</Label><Input value={data.arrivalTerminal} onChange={e => onChange('arrivalTerminal', e.target.value)} className="mt-1" /></div>
      <div><Label>Arrival Date</Label><Input type="date" value={data.arrivalDate} onChange={e => onChange('arrivalDate', e.target.value)} className="mt-1" /></div>
      <div><Label>Arrival Time</Label><Input type="time" value={data.arrivalTime} onChange={e => onChange('arrivalTime', e.target.value)} className="mt-1" /></div>
      <div><Label>Baggage</Label><Input value={data.baggage} onChange={e => onChange('baggage', e.target.value)} className="mt-1" placeholder="e.g. 20kg + 7kg" /></div>
      <div><Label>Seat</Label><Input value={data.seatNumber} onChange={e => onChange('seatNumber', e.target.value)} className="mt-1" /></div>
    </div>
  </Card>
);

const HotelFields = ({ data, onChange }) => (
  <Card className="p-6" data-testid="hotel-fields">
    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Hotel Details</h3>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      <div className="col-span-2"><Label>Hotel Name *</Label><Input value={data.hotelName} onChange={e => onChange('hotelName', e.target.value)} className="mt-1" required /></div>
      <div><Label>Confirmation #</Label><Input value={data.confirmationNumber} onChange={e => onChange('confirmationNumber', e.target.value)} className="mt-1" /></div>
      <div className="col-span-2"><Label>Address</Label><Input value={data.address} onChange={e => onChange('address', e.target.value)} className="mt-1" /></div>
      <div><Label>Contact Phone</Label><Input value={data.contactPhone} onChange={e => onChange('contactPhone', e.target.value)} className="mt-1" /></div>
      <div><Label>Check-in</Label><Input type="date" value={data.checkIn} onChange={e => onChange('checkIn', e.target.value)} className="mt-1" /></div>
      <div><Label>Check-out</Label><Input type="date" value={data.checkOut} onChange={e => onChange('checkOut', e.target.value)} className="mt-1" /></div>
      <div><Label>Room Type</Label><Input value={data.roomType} onChange={e => onChange('roomType', e.target.value)} className="mt-1" placeholder="e.g. Deluxe Sea View" /></div>
      <div><Label>Meal Plan</Label>
        <Select value={data.mealPlan || 'none'} onValueChange={v => onChange('mealPlan', v)}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No Meals</SelectItem>
            <SelectItem value="BB">Bed & Breakfast</SelectItem>
            <SelectItem value="HB">Half Board</SelectItem>
            <SelectItem value="FB">Full Board</SelectItem>
            <SelectItem value="AI">All Inclusive</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2"><Label>Special Requests</Label><Input value={data.specialRequests} onChange={e => onChange('specialRequests', e.target.value)} className="mt-1" /></div>
    </div>
  </Card>
);

const ActivityFields = ({ data, onChange }) => (
  <Card className="p-6" data-testid="activity-fields">
    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Activity / Sightseeing Details</h3>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      <div className="col-span-2"><Label>Activity Name *</Label><Input value={data.activityName} onChange={e => onChange('activityName', e.target.value)} className="mt-1" required /></div>
      <div><Label>Voucher Type</Label><Input value={data.voucherType || ''} onChange={e => onChange('voucherType', e.target.value)} className="mt-1" placeholder="Open Date / Specific Date" /></div>
      <div><Label>Date</Label><Input type="date" value={data.date} onChange={e => onChange('date', e.target.value)} className="mt-1" /></div>
      <div><Label>Time</Label><Input type="time" value={data.time} onChange={e => onChange('time', e.target.value)} className="mt-1" /></div>
      <div><Label>Duration</Label><Input value={data.duration} onChange={e => onChange('duration', e.target.value)} className="mt-1" placeholder="e.g. 3 hours" /></div>
      <div><Label>Validity</Label><Input value={data.validity || ''} onChange={e => onChange('validity', e.target.value)} className="mt-1" placeholder="e.g. Valid for 30 days" /></div>
      <div><Label>Reporting Time</Label><Input value={data.reportingTime || ''} onChange={e => onChange('reportingTime', e.target.value)} className="mt-1" placeholder="e.g. 30 mins prior" /></div>
      <div><Label>Lead Guest Name</Label><Input value={data.leadGuestName || ''} onChange={e => onChange('leadGuestName', e.target.value)} className="mt-1" /></div>
      <div><Label>Total Pax</Label><Input type="number" value={data.totalPax || ''} onChange={e => onChange('totalPax', e.target.value)} className="mt-1" /></div>
      <div><Label>Adults</Label><Input type="number" value={data.adults || ''} onChange={e => onChange('adults', e.target.value)} className="mt-1" /></div>
      <div><Label>Children</Label><Input type="number" value={data.children || ''} onChange={e => onChange('children', e.target.value)} className="mt-1" /></div>
      <div><Label>Voucher Ref</Label><Input value={data.voucherRef || ''} onChange={e => onChange('voucherRef', e.target.value)} className="mt-1" placeholder="Travcations ref" /></div>
      <div><Label>Partner Ref</Label><Input value={data.partnerRef || ''} onChange={e => onChange('partnerRef', e.target.value)} className="mt-1" /></div>
      <div><Label>Operator Ref</Label><Input value={data.operatorRef || ''} onChange={e => onChange('operatorRef', e.target.value)} className="mt-1" /></div>
      <div className="col-span-2"><Label>Pickup Point</Label><Input value={data.pickupPoint} onChange={e => onChange('pickupPoint', e.target.value)} className="mt-1" /></div>
      <div><Label>Hotline</Label><Input value={data.hotline || ''} onChange={e => onChange('hotline', e.target.value)} className="mt-1" /></div>
      <div className="col-span-3"><Label>Address / Venue</Label><Input value={data.address || ''} onChange={e => onChange('address', e.target.value)} className="mt-1" /></div>
      <div className="col-span-3"><Label>Description / Overview</Label><Input value={data.description} onChange={e => onChange('description', e.target.value)} className="mt-1" /></div>
      <div className="col-span-3"><Label>Inclusions</Label><Input value={data.inclusions} onChange={e => onChange('inclusions', e.target.value)} className="mt-1" placeholder="Comma-separated inclusions" /></div>
      <div className="col-span-3"><Label>Cancellation Policy</Label><Input value={data.cancellationPolicy || ''} onChange={e => onChange('cancellationPolicy', e.target.value)} className="mt-1" placeholder="Cancellation terms" /></div>
    </div>
  </Card>
);

const TransferFields = ({ data, onChange }) => (
  <Card className="p-6" data-testid="transfer-fields">
    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Transfer Details</h3>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      <div><Label>Transfer Type</Label>
        <Select value={data.transferType} onValueChange={v => onChange('transferType', v)}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="airport">Airport Transfer</SelectItem>
            <SelectItem value="city">City Transfer</SelectItem>
            <SelectItem value="intercity">Intercity Transfer</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div><Label>Pickup</Label><Input value={data.pickup} onChange={e => onChange('pickup', e.target.value)} className="mt-1" /></div>
      <div><Label>Drop-off</Label><Input value={data.drop} onChange={e => onChange('drop', e.target.value)} className="mt-1" /></div>
      <div><Label>Date</Label><Input type="date" value={data.date} onChange={e => onChange('date', e.target.value)} className="mt-1" /></div>
      <div><Label>Time</Label><Input type="time" value={data.time} onChange={e => onChange('time', e.target.value)} className="mt-1" /></div>
      <div><Label>Vehicle Type</Label><Input value={data.vehicleType} onChange={e => onChange('vehicleType', e.target.value)} className="mt-1" placeholder="e.g. Sedan, SUV" /></div>
      <div><Label>Driver Name</Label><Input value={data.driverName} onChange={e => onChange('driverName', e.target.value)} className="mt-1" /></div>
      <div><Label>Driver Phone</Label><Input value={data.driverPhone} onChange={e => onChange('driverPhone', e.target.value)} className="mt-1" /></div>
    </div>
  </Card>
);

const ItineraryFields = ({ data, onChange }) => (
  <Card className="p-6" data-testid="itinerary-fields">
    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Itinerary Details</h3>
    <div className="grid grid-cols-1 gap-4">
      <div><Label>Linked Itinerary ID</Label><Input value={data.itineraryId} onChange={e => onChange('itineraryId', e.target.value)} className="mt-1" placeholder="e.g. ITN_0226_001" /></div>
      <div><Label>Summary</Label>
        <textarea value={data.summary} onChange={e => onChange('summary', e.target.value)}
          className="mt-1 w-full min-h-[120px] border rounded-md p-3 text-sm" placeholder="Day-wise summary of the itinerary..." />
      </div>
    </div>
  </Card>
);

export default VoucherForm;
